import {observer_mgl} from "mobx-graphlink";
import {MapObject} from "../../../../../Server/DB/mapObjects/@MapObject.js";
import {DMap} from "../../../../../Server/DB/maps/@Map.js";
import {MapState} from "../../../../../Store/main/maps/@MapState.js";
import {dreamCellLetters, DreamScene, GetDreamAnchorEntity, GetDreamSceneForJournalPath, GetLastXDreamScenesPerLetterAnchor, GetScenesValidToBeLetterAnchors, GetStandardDeepOrderingKeyForDreamScene} from "../../../../../Server/DB/journalEntries/$journalEntry.js";
import React from "react";
import {GetEntity} from "../../../../../Server/DB/entities.js";
import {useBase64ImageStrAsTexture} from "../../../../../Utils/ThreeJS/3JSHooks.js";
import {mapZIndexes} from "../MapZIndexes.js";
import {Vec3} from "../../../../../Utils/ThreeJS/VectorStructs.js";
import {observer_mgl_r3} from "../../../../../Utils/ThreeJS/General.js";
import {useMeterToPixelRatio} from "./AdaptiveLabel.js";
import {useThree} from "@react-three/fiber";
import {GetJournalEntries} from "../../../../../Server/DB/journalEntries.js";
import {MeID} from "../../../../../Server/DB/users.js";
import {AssertUnreachable} from "web-vcore";
import {AnchorDisplayLayer} from "../../../../../Store/main/maps/@MapState/MapSettings.js";

export const AnchorEntityMapIcons = observer_mgl_r3((props: {obj: MapObject, map: DMap, mapState: MapState})=>{
	const {obj, map, mapState} = props;
	const settings = mapState.settings;
	if (!settings.general.showAnchors) return null;
	if (mapState.settings.general.screenshotMode) return null;

	const letterAnchorScenesToShowIconsFor_byLetter = GetLastXDreamScenesPerLetterAnchor(settings.general.anchors_historyDepth);
	const letterAnchorScenesToShowIconsFor_flat_journalPaths = new Set([...letterAnchorScenesToShowIconsFor_byLetter.values()].flatMap(a=>a).map(a=>a.ID()));
	const objJournalPathsToShowIconsFor = (obj.journalSegmentPaths ?? []).filter(path=>letterAnchorScenesToShowIconsFor_flat_journalPaths.has(path))
	if (objJournalPathsToShowIconsFor.length == 0) return null;

	return objJournalPathsToShowIconsFor.map((journalPath, index)=>{
		const scene = GetDreamSceneForJournalPath(journalPath);
		if (scene == null) return null;
		return (
			<AnchorEntityMapIcon key={journalPath} scene={scene} obj={obj} map={map} mapState={mapState} index={index}/>
		);
	});
});

export const AnchorEntityMapIcon = observer_mgl_r3((props: {scene: DreamScene, obj: MapObject, map: DMap, mapState: MapState, index: number})=>{
	const {scene, obj, map, mapState, index} = props;
	const settings = mapState.settings;
	const letterAnchorID = GetDreamAnchorEntity(scene.segment, scene.sceneIndex ?? 0);
	const letterAnchor = GetEntity(letterAnchorID);
	const imgSrc = letterAnchor?.image_embedded || letterAnchor?.url || null;
	const texture = useBase64ImageStrAsTexture(imgSrc);

	const {camera: camera_base, size: canvasSize} = useThree();
	const meterToPixelRatio = useMeterToPixelRatio(mapState);

	if (letterAnchor == null || imgSrc == null) return null;

	const iconWidthAsPercent = settings.general.anchors_size;
	const iconWidthAsPixels = iconWidthAsPercent * canvasSize.width;
	const iconWidthAsMeters = iconWidthAsPixels / meterToPixelRatio;
	const iconHeightAsMeters = texture && !isNaN(texture.image.height / texture.image.width)
		? iconWidthAsMeters * (texture.image.height / texture.image.width)
		: iconWidthAsMeters;
	const iconXOffset_inIconWidths = -.5 - index;
	const iconYOffset_inIconHeights = .5;

	//const letter = letterAnchor?.name?.[0]?.toLowerCase() || "?";
	const entries = GetJournalEntries(MeID());
	const validScenes = GetScenesValidToBeLetterAnchors(entries);
	const lastValidScene_index = validScenes.length - 1;
	// have newest dreams show up on top
	const offsetFromLatestGridScene = validScenes.findIndex(a=>a.ID() == scene.ID()) - lastValidScene_index;
	const layer = settings.general.anchors_layer;
	const zIndex_minNoConflict = 100; // keep >=100, else could drop under other map-element categories
	const zIndex =
		layer == AnchorDisplayLayer.low ? mapZIndexes.anchorIcons_low_last + offsetFromLatestGridScene :
		layer == AnchorDisplayLayer.mid ? (mapZIndexes.anchorIcons_mid_last + offsetFromLatestGridScene).KeepAtLeast(zIndex_minNoConflict) :
		layer == AnchorDisplayLayer.high ? (mapZIndexes.anchorIcons_high_last + offsetFromLatestGridScene).KeepAtLeast(zIndex_minNoConflict) :
		AssertUnreachable(layer);
	const iconPos = new Vec3(iconXOffset_inIconWidths * iconWidthAsMeters, iconYOffset_inIconHeights * iconHeightAsMeters, zIndex).To3JPos();

	return (
		<mesh position={iconPos}
			// for some reason this is needed, to show above the AdaptiveLevel text itself
			renderOrder={layer == "high" ? 3 : 0}
		>
			<planeGeometry args={[iconWidthAsMeters, iconHeightAsMeters]}/>
			<meshBasicMaterial map={texture}
				// these are needed so, in "high" layering, it can show above AdaptiveLabel background
				transparent={layer == "high"} depthTest={layer != "high"}/>
		</mesh>
	);
});